import { useState } from "react";
import type { ChangeEvent, FormEvent } from "react";
import "../styles/contact.style.css";
import { CONTACT_INFO } from "../constants/links.constant";
import { ContactFormOutputModel } from "../models/contact-form.model";
import type { ContactFormErrors } from "../models/contact-form.model";
import { useRecaptcha } from "../hooks/use-recaptcha.hook";

export function Contact() {
  const executeRecaptcha = useRecaptcha();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [fieldErrors, setFieldErrors] = useState<ContactFormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  function handleChange(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { name, value } = e.target;
    if (name === "message" && value.length > 100) return;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (name in fieldErrors) setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setFieldErrors({});
    setSubmitError(null);
    try {
      const recaptchaToken = await executeRecaptcha();
      if (!recaptchaToken) {
        setSubmitError("Please complete the reCAPTCHA verification.");
        return;
      }
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, recaptchaToken }),
      });
      if (!res.ok) {
        const parsed = ContactFormOutputModel.safeParse(await res.json());
        if (parsed.success && parsed.data.errors) {
          setFieldErrors(parsed.data.errors);
        } else if (parsed.success && parsed.data.message) {
          setSubmitError(parsed.data.message);
        } else {
          setSubmitError("Something went wrong. Please try again.");
        }
        return;
      }
      setSubmitted(true);
    } catch {
      setSubmitError("Network error. Please check your connection and try again.");
    }
  }

  return (
    <section id="contact" className="pb-20 px-6 md:pb-35 md:px-13">
      <div className="max-w-6xl mx-auto border-t border-border pt-20 md:pt-35 grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20 items-start">
        <div>
          <p className="section-label reveal">Contact</p>
          <h2 className="text-[clamp(32px,4.5vw,64px)] font-bold tracking-[-0.02em] leading-[1.05] text-primary mb-8 reveal">
            Let&apos;s
            <br />
            connect.
          </h2>
          <a
            href={CONTACT_INFO.href}
            className="group flex items-center justify-between mt-10 py-6 border-b border-border-light no-underline text-primary hover:pl-3 transition-[padding-left] duration-300 reveal"
          >
            <div className="flex flex-col gap-1.5">
              <span className="text-[10px] tracking-[0.25em] uppercase text-muted">
                {CONTACT_INFO.label}
              </span>
              <span className="text-[15px] font-bold tracking-wider">{CONTACT_INFO.value}</span>
            </div>
            <span className="transition-transform duration-300 group-hover:translate-x-1.5">→</span>
          </a>
        </div>

        <div>
          <form
            onSubmit={handleSubmit}
            style={submitted ? { opacity: 0.3, pointerEvents: "none" } : undefined}
          >
            <div className="mb-7 reveal">
              <label className="block text-[10px] tracking-[0.25em] uppercase text-muted mb-2.5">
                Name
              </label>
              <input
                className="field-input"
                type="text"
                name="name"
                placeholder="Your name"
                value={form.name}
                onChange={handleChange}
                required
                autoComplete="off"
              />
              {fieldErrors.name && (
                <span className="block mt-1.5 text-[11px] text-error">{fieldErrors.name}</span>
              )}
            </div>
            <div className="mb-7 reveal">
              <label className="block text-[10px] tracking-[0.25em] uppercase text-muted mb-2.5">
                Email
              </label>
              <input
                className="field-input"
                type="email"
                name="email"
                placeholder="your@email.com"
                value={form.email}
                onChange={handleChange}
                required
                autoComplete="off"
              />
              {fieldErrors.email && (
                <span className="block mt-1.5 text-[11px] text-error">{fieldErrors.email}</span>
              )}
            </div>
            <div className="mb-7 reveal">
              <label className="block text-[10px] tracking-[0.25em] uppercase text-muted mb-2.5">
                Message
              </label>
              <textarea
                className="field-input field-textarea"
                name="message"
                placeholder="Your message..."
                value={form.message}
                onChange={handleChange}
                required
              />
              {fieldErrors.message && (
                <span className="block mt-1.5 text-[11px] text-error">{fieldErrors.message}</span>
              )}
              <span className="block text-right mt-1.5 text-[11px] text-muted">
                {form.message.length} / 100
              </span>
            </div>
            <div id="recaptcha-container" className="mb-7 reveal" />
            <div className="reveal">
              <button
                type="submit"
                className="bg-transparent border border-border text-primary text-xs tracking-[0.2em] uppercase py-3.5 px-8 cursor-pointer transition-colors duration-200 hover:bg-primary hover:text-bg hover:border-primary"
              >
                Send message →
              </button>
              {submitError && <p className="mt-3 text-[11px] text-error">{submitError}</p>}
            </div>
          </form>
          {submitted && (
            <p className="text-[12px] tracking-[0.15em] text-muted mt-6">
              Message sent. Thank you.
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
